import React from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { BLOG_POSTS, getPostBySlug } from "@/data/blog";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import RelatedContent from "@/components/RelatedContent";
import CTABlock from "@/components/CTABlock";
import { organizationSchema, websiteSchema, articleSchema, breadcrumbSchema, webPageSchema } from "@/lib/schemas";

export const BlogIndex = () => {
  useSeo(SEO.blog);
  return (
    <main data-testid="blog-page" className="bg-black">
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Notes" }]} />
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92]">
            Notes.
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-lg">
            Plain-English notes on brand, website, and being easier to choose. Short. Useful. No filler.
          </p>
        </div>
      </section>

      {/* Collection promo: Brand & Branding */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-14">
          <Link
            to="/brand-branding"
            data-testid="blog-brand-branding-promo"
            className="group block border-2 border-[#FFC600] p-6 lg:p-10 hover:bg-[#FFC600] hover:text-black transition-colors"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-8">
                <Overline color="yellow" className="mb-3 group-hover:text-black">
                  Collection · 32 essays
                </Overline>
                <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
                  Brand <span className="k-yellow group-hover:text-black">&</span> Branding.
                </h2>
                <p className="mt-4 text-white/75 group-hover:text-black/85 max-w-2xl text-base lg:text-lg leading-relaxed">
                  A connected educational system on what a brand actually is, how strategy works, how visual and verbal identity operate, how a brand is experienced, and how it is managed over time. Thirty-two essays across six clusters.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[#FFC600] group-hover:text-black">
                  Open the collection <span aria-hidden>→</span>
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      <section className="bg-black">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {BLOG_POSTS.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group block border border-white/15 p-6 lg:p-8 hover:k-hard-shadow-yellow"
            >
              <Overline className="mb-3">Note</Overline>
              <h2 className="text-2xl lg:text-3xl font-black tracking-tighter leading-tight">{p.title}</h2>
              <p className="mt-3 text-white/70 text-sm">{p.excerpt}</p>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-xs text-white/50 uppercase tracking-[0.18em]">{p.readTime}</span>
                <span className="text-sm font-semibold group-hover:text-[#FFC600]">Read →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <CTABlock variant="default" />
    </main>
  );
};

export const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const path = post ? `/blog/${post.slug}` : "/blog";
  const title = post ? `${post.title} | Notes | Kostaja` : "Note | Kostaja";
  const description = post?.excerpt || "";
  useSeo({
    title,
    description,
    canonical: `https://kostaja.ca${path}`,
    ogType: "article",
    jsonLd: post
      ? [
          organizationSchema(),
          websiteSchema(),
          webPageSchema({ path, name: title, description }),
          articleSchema({ path, title: post.title, description: post.excerpt }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Notes", path: "/blog" },
            { name: post.title, path },
          ]),
        ]
      : [],
  });

  if (!post) {
    return (
      <main className="bg-black">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center">
          <h1 className="text-5xl font-black tracking-tighter">Note not found.</h1>
          <div className="mt-8 flex justify-center gap-3">
            <SecondaryButton to="/blog">All notes</SecondaryButton>
            <PrimaryButton to="/contact">Contact</PrimaryButton>
          </div>
        </div>
      </main>
    );
  }
  return (
    <main data-testid="blog-post" className="bg-black">
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { to: "/blog", label: "Notes" }, { label: post.title }]} />
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl">{post.title}</h1>
          <div className="mt-5 text-white/60 text-xs uppercase tracking-[0.22em]">{post.readTime}</div>
        </div>
      </section>
      <section className="bg-black border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <p className="text-xl lg:text-2xl font-medium leading-snug">{post.excerpt}</p>
          <div className="mt-10 space-y-6 text-lg leading-relaxed text-white/85">
            {post.body.map((para, idx) => <p key={`${post.slug}-p-${idx}`}>{para}</p>)}
          </div>
        </div>
      </section>
      <RelatedContent
        serviceSlugs={post.relatedServices || []}
        needSlugs={post.relatedNeeds || []}
        heading="Where this matters"
      />
      <CTABlock variant="blog" />
    </main>
  );
};
